import { Injectable } from '@angular/core';
import { filter, Subscription, take } from 'rxjs';
import { DocumentScope } from '../models/response-document-subtype';
import { DocumentProcessingStepEnum, ResponseProjectUploadFile } from '../models/response-project-document';
import {
  UPLOAD_CONCURRENCY,
  UPLOAD_PROGRESS_MIN_DURATION_MS,
  WS_ERROR_REASON_MAP,
  WS_FALLBACK_ERROR_REASON,
  WS_REASON_CONFLICT_EXPIRED,
} from './document-import.constants';
import { DocumentImportContext, DocumentImportErrorReason, DocumentImportService } from './document-import.service';
import { ImportConflictSession } from './import-conflict-session';
import { TendersResponsesService, WsConnection } from './tenders-responses.service';

export interface ProjectImportRunOptions {
  projectId: string;
  scope: DocumentScope;
  ctx: DocumentImportContext;
  files: ResponseProjectUploadFile[];
  defaultSubtypeId?: number;
  conflicts: ImportConflictSession;
  /** When false, WS conflict_key messages are treated as generic errors. */
  dedupEnabled?: boolean;
  getFiles: () => ResponseProjectUploadFile[];
  presentPieceByName: (name: string) => ResponseProjectUploadFile | undefined;
  subtypeIdOf: (file?: ResponseProjectUploadFile) => number | undefined;
  onConflict: (conflictKey: string, name: string, existingId?: number, existingSubtypeId?: number) => void;
  onConflictExpired: (conflictKey: string) => void;
  removeConflict: (conflictKey: string) => void;
  maybeCloseWs: () => void;
  ignoreFile: (name: string, reason: string) => void;
  onUploadError: (file: ResponseProjectUploadFile, stage: 'create' | 'upload', reason: DocumentImportErrorReason) => void;
  onCategorizing?: () => void;
  onCategorized?: () => void;
  onProcessingComplete?: () => void;
  extraSubscriptions?: (wsConn: WsConnection) => Subscription[];
}

export interface ProjectImportRunResult {
  wsConn: WsConnection | null;
  subscriptions: Subscription[];
}

@Injectable({ providedIn: 'root' })
export class ProjectDocumentImportService {
  constructor(
    private documentImport: DocumentImportService,
    private tendersResponses: TendersResponsesService
  ) {}

  /**
   * Creates the document row then uploads to S3, patching the project upload store.
   */
  uploadToProject(
    projectId: string,
    scope: DocumentScope,
    ctx: DocumentImportContext,
    file: ResponseProjectUploadFile,
    onError: (stage: 'create' | 'upload', reason: DocumentImportErrorReason) => void
  ): Promise<void> {
    const source = file.sourceFile;
    if (!(source instanceof File)) return Promise.resolve();

    this.tendersResponses.patchFile(projectId, scope, file.uid, {
      uploadProgress: 0,
      processingStep: DocumentProcessingStepEnum.UPLOADING,
    });

    return new Promise((resolve, reject) => {
      this.documentImport.uploadFile(ctx, file, { replaceId: file.replaceId }).subscribe(event => {
        switch (event.type) {
          case 'created':
            this.tendersResponses.patchFile(projectId, scope, file.uid, {
              uid: event.uid,
              documentId: event.documentId,
              isInLibrary: false,
            });
            file.uid = event.uid;
            file.documentId = event.documentId;
            break;

          case 'progress':
            this.tendersResponses.patchFile(projectId, scope, file.uid, { uploadProgress: event.progress });
            break;

          case 'uploaded':
            this.tendersResponses.incrementUploadCompleted(projectId, scope);
            setTimeout(() => {
              this.tendersResponses.patchFile(projectId, scope, file.uid, {
                uploadProgress: undefined,
                sourceFile: undefined,
              });
              delete file.sourceFile;
            }, UPLOAD_PROGRESS_MIN_DURATION_MS);
            resolve();
            break;

          case 'error':
            onError(event.stage, event.reason);
            reject(new Error('document import failed'));
            break;
        }
      });
    });
  }

  /**
   * Archive-first upload, WS processing, and shared conflict/error subscriptions.
   * Returns once the socket is wired — processing itself continues in the background.
   */
  async run(options: ProjectImportRunOptions): Promise<ProjectImportRunResult> {
    const { projectId, scope, files, defaultSubtypeId } = options;
    const { archives: zipFiles, regular: regularFiles } = this.documentImport.splitArchives(files);
    const filesToUploadNow = zipFiles.length ? zipFiles : regularFiles;

    this.tendersResponses.setUploadBatch(projectId, scope, filesToUploadNow.length, zipFiles.length ? 0 : filesToUploadNow.length);

    const uploadOne = (file: ResponseProjectUploadFile) =>
      this.uploadToProject(projectId, scope, { ...options.ctx, defaultSubtypeId }, file, (stage, reason) =>
        options.onUploadError(file, stage, reason)
      );

    await this.documentImport.runWithConcurrency(filesToUploadNow, UPLOAD_CONCURRENCY, f => uploadOne(f));

    const uploadedZips = zipFiles.filter(f => f.documentId != null);
    const regularToProcess = zipFiles.length ? regularFiles : regularFiles.filter(f => f.documentId != null);

    if (!uploadedZips.length && !regularToProcess.length) {
      this.tendersResponses.resetUploadBatch(projectId, scope);
      return { wsConn: null, subscriptions: [] };
    }

    const wsConn = this.tendersResponses.connectToProcessing();
    const subscriptions: Subscription[] = [];

    for (const zf of uploadedZips) {
      this.tendersResponses.patchFile(projectId, scope, zf.uid, {
        processingStep: DocumentProcessingStepEnum.EXTRACTING,
      });
    }

    const initialPayload = this.documentImport.buildProcessingPayload(options.ctx, uploadedZips.length ? uploadedZips : regularToProcess);

    if (uploadedZips.length && regularFiles.length) {
      let downloadingCount = 0;
      let regularSent = false;
      subscriptions.push(
        wsConn.messages$
          .pipe(
            filter(msg => msg.status === 'Downloading archive'),
            take(uploadedZips.length)
          )
          .subscribe(() => {
            downloadingCount++;
            if (downloadingCount >= uploadedZips.length && !regularSent) {
              regularSent = true;
              this.tendersResponses.setUploadBatch(projectId, scope, regularFiles.length);
              void this.documentImport
                .runWithConcurrency(regularFiles, UPLOAD_CONCURRENCY, f => uploadOne(f))
                .then(() => {
                  const uploaded = regularFiles.filter(f => f.documentId != null);
                  if (uploaded.length) {
                    wsConn.sendPayload(this.documentImport.buildProcessingPayload(options.ctx, uploaded));
                  }
                });
            }
          })
      );
    }

    this.tendersResponses.startWsProcessing(wsConn, projectId, scope, initialPayload, () => options.conflicts.hasPending);

    if (options.onCategorizing) {
      subscriptions.push(
        wsConn.messages$
          .pipe(
            filter(msg => msg.status === 'categorizing'),
            take(1)
          )
          .subscribe(() => options.onCategorizing?.())
      );
    }

    if (options.onCategorized) {
      subscriptions.push(
        wsConn.messages$
          .pipe(
            filter(msg => msg.status === 'categorized'),
            take(1)
          )
          .subscribe(() => options.onCategorized?.())
      );
    }

    subscriptions.push(
      wsConn.messages$
        .pipe(
          filter(msg => msg.status === 'processing_complete'),
          take(1)
        )
        .subscribe(() => options.onProcessingComplete?.())
    );

    subscriptions.push(...this.bindConflictSubscriptions(wsConn, options));

    if (options.extraSubscriptions) {
      subscriptions.push(...options.extraSubscriptions(wsConn));
    }

    return { wsConn, subscriptions };
  }

  private bindConflictSubscriptions(wsConn: WsConnection, options: ProjectImportRunOptions): Subscription[] {
    const { projectId, scope, conflicts } = options;
    const dedupEnabled = options.dedupEnabled !== false;
    const subs: Subscription[] = [];

    subs.push(
      wsConn.messages$.pipe(filter(msg => msg.status === 'error')).subscribe(msg => {
        if (msg.status !== 'error') return;

        if (msg.conflict_key && msg.reason !== WS_REASON_CONFLICT_EXPIRED && dedupEnabled) {
          const name = msg.filename ?? '';
          const piece = options.presentPieceByName(name);
          options.onConflict(msg.conflict_key, name, piece?.documentId, options.subtypeIdOf(piece));
          return;
        }

        if (msg.reason === WS_REASON_CONFLICT_EXPIRED && msg.conflict_key) {
          options.onConflictExpired(msg.conflict_key);
          return;
        }

        const translationKey = WS_ERROR_REASON_MAP[msg.reason];
        const failedKey = msg.filename ? conflicts.findKeyByName(msg.filename) : undefined;
        const uid = msg.uid ?? options.getFiles().find(f => f.name === msg.filename)?.uid;
        const name = msg.filename ?? (failedKey && conflicts.get(failedKey)?.name) ?? '';

        if (!translationKey && !name) {
          options.maybeCloseWs();
          return;
        }

        options.ignoreFile(name, translationKey ?? WS_FALLBACK_ERROR_REASON);
        if (uid) this.tendersResponses.removeFile(projectId, scope, uid);
        if (failedKey) options.removeConflict(failedKey);
        else options.maybeCloseWs();
      })
    );

    subs.push(
      wsConn.messages$.pipe(filter(msg => msg.status === 'conflict_skipped')).subscribe(msg => {
        if (msg.status !== 'conflict_skipped') return;
        options.removeConflict(msg.conflict_key);
      })
    );

    subs.push(
      wsConn.messages$.pipe(filter(msg => msg.status === 'uploaded')).subscribe(msg => {
        if (msg.status !== 'uploaded') return;
        const key = conflicts.findKeyByName(msg.filename, 'replace');
        if (!key) return;
        const entry = conflicts.get(key);
        const files = options.getFiles();
        const transientRow = entry?.existingId != null && files.some(f => f.documentId === entry.existingId && f.uid !== msg.uid);
        if (transientRow) {
          this.tendersResponses.removeFile(projectId, scope, msg.uid);
        } else {
          const row = files.find(f => f.uid === msg.uid);
          this.tendersResponses.patchFile(projectId, scope, msg.uid, {
            processingStep: row?.subtype ? DocumentProcessingStepEnum.CATEGORIZED : DocumentProcessingStepEnum.UNCATEGORIZED,
          });
        }
        options.removeConflict(key);
      })
    );

    return subs;
  }

  /**
   * Replaces an existing project document with a client file. Appends a transient
   * row, then uploads in place. Returns `pending` when the project is not created yet.
   */
  queueReplace(options: {
    projectId: string | null;
    scope: DocumentScope;
    ctx: DocumentImportContext;
    uid: string;
    file: File;
    replaceId: number;
    subtype?: string;
    customName?: string | null;
    existingUid?: string;
    onError: (name: string) => void;
  }): 'pending' | 'started' {
    const storeId = options.projectId ?? 'new';
    if (options.existingUid) {
      this.tendersResponses.removeFile(storeId, options.scope, options.existingUid);
    }
    this.tendersResponses.appendFile(storeId, options.scope, {
      uid: options.uid,
      name: options.file.name,
      size: options.file.size,
      sourceFile: options.file,
      replaceId: options.replaceId,
      subtype: options.subtype,
      customName: options.customName,
      processingStep: DocumentProcessingStepEnum.UPLOADING,
      uploadProgress: 0,
      isInLibrary: false,
    });

    if (!options.projectId) return 'pending';

    const row = this.tendersResponses
      .uploadStates()
      [this.tendersResponses.fileKey(options.projectId, options.scope)]?.files.find(f => f.uid === options.uid);
    if (!row) return 'started';

    const projectId = options.projectId;
    this.documentImport.uploadFile(options.ctx, row, { replaceId: options.replaceId }).subscribe(event => {
      switch (event.type) {
        case 'created':
          this.tendersResponses.patchFile(projectId, options.scope, row.uid, {
            uid: event.uid,
            documentId: event.documentId,
            isInLibrary: false,
          });
          row.uid = event.uid;
          row.documentId = event.documentId;
          break;
        case 'progress':
          this.tendersResponses.patchFile(projectId, options.scope, row.uid, { uploadProgress: event.progress });
          break;
        case 'uploaded':
          this.tendersResponses.patchFile(projectId, options.scope, row.uid, {
            uploadProgress: undefined,
            sourceFile: undefined,
            processingStep: options.subtype ? DocumentProcessingStepEnum.CATEGORIZED : DocumentProcessingStepEnum.UNCATEGORIZED,
          });
          break;
        case 'error':
          this.tendersResponses.removeFile(projectId, options.scope, row.uid);
          options.onError(row.name);
          break;
      }
    });
    return 'started';
  }
}
